import { useState, useEffect } from "react";
import { MercureEvent } from "./type";
import { logger } from "@/services/Logger";
import { useAppSelector } from "@/redux/features/hooks";
import {
  buildMercureUrl,
  getMercureToken,
  invalidateMercureToken,
} from "./mercure";

export function useToggle() {
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const toggle = () => setIsOpen((isOpen) => !isOpen);
  const open = () => setIsOpen(true);
  const close = () => setIsOpen(false);

  return {
    isOpen,
    toggle,
    close,
    open,
  };
}

export function useModal<T>() {
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [item, setItem] = useState<T>();

  const openModal = () => {
    setIsOpen(true);
    setItem(undefined);
  };

  const closeModal = () => {
    setItem(undefined);
    setIsOpen(false);
  };

  const resetModalItem = () => setItem(undefined);

  const openEditModal = (item: T) => {
    setItem(item);
    setIsOpen(true);
  };

  return {
    isOpen,
    item,
    closeModal,
    openModal,
    openEditModal,
    resetModalItem,
  };
}

export const usePagination = (limit: number) => {
  const [perPage, setPerPage] = useState<number>(limit);
  const [page, setPage] = useState<number>(1);

  const onChange = (perPage: number, page?: number) => {
    setPerPage(perPage);
    if (page) {
      setPage(page);
    } else {
      setPage(1);
    }
  };

  return {
    page,
    limit: perPage,
    onChange,
  };
};

const DEFAULT_MERCURE_EVENTS = ["order:created", "order:updated"] as const;

export function useMercureSubscription<T>(
  topic: string | null,
  events: readonly string[] = DEFAULT_MERCURE_EVENTS,
): MercureEvent<T> | null {
  // The subscriber JWT is scoped to this restaurant (SFAPI-121).
  const restaurantId = useAppSelector(
    (state) => state.user.currentRestaurant?.id
  );
  const [event, setEvent] = useState<MercureEvent<T> | null>(null);
  // Stabilize the dependency so callers can pass an inline array literal
  // without re-subscribing on every render.
  const eventsKey = events.join(",");

  useEffect(() => {
    if (!topic) {
      return;
    }
    const eventNames = eventsKey.split(",").filter(Boolean);

    let eventSource: EventSource | null = null;
    let reconnectTimer: NodeJS.Timeout | null = null;
    let connectionAttempts = 0;
    const maxRetries = 10;
    const baseDelay = 2000;
    let isReconnecting = false;
    let isCancelled = false;
    // Only the latest connect() may open an EventSource: the token fetch is
    // async, so two calls (e.g. "online" + "visibilitychange") can overlap.
    let connectSeq = 0;

    const connect = async () => {
      if (isReconnecting || isCancelled) return;
      const attempt = ++connectSeq;

      const token = await getMercureToken(restaurantId);
      if (isCancelled || attempt !== connectSeq) return;

      try {
        if (eventSource) {
          eventSource.close();
        }
        eventSource = new EventSource(buildMercureUrl(topic, token));

        const handleEvent = (type: string) => (e: MessageEvent) => {
          try {
            const eventData = JSON.parse(e.data) as T;
            setEvent({ type, data: eventData });
          } catch (error) {
            logger.error(
              "useMercureSubscription",
              "Error parsing Mercure event data:",
              error
            );
          }
        };

        eventSource.onopen = () => {
          logger.info(
            "useMercureSubscription",
            "Mercure subscription connected for topic:",
            topic
          );
          connectionAttempts = 0;
          isReconnecting = false;
        };

        for (const name of eventNames) {
          eventSource.addEventListener(name, handleEvent(name));
        }

        eventSource.onerror = (error: Event) => {
          logger.error(
            "useMercureSubscription",
            "Mercure EventSource error:",
            error
          );
          // The hub may have refused the token: fetch a new one next time.
          invalidateMercureToken(restaurantId);
          handleConnectionError();
        };
      } catch (error) {
        logger.error(
          "useMercureSubscription",
          "Failed to create EventSource:",
          error
        );
        handleConnectionError();
      }
    };

    const handleConnectionError = () => {
      connectionAttempts++;

      if (connectionAttempts <= maxRetries) {
        const delay = Math.min(
          baseDelay * Math.pow(1.5, connectionAttempts - 1),
          30000
        );

        logger.info(
          "useMercureSubscription",
          `Reconnecting Mercure subscription (${connectionAttempts}/${maxRetries}) in ${delay}ms...`
        );

        isReconnecting = true;
        reconnectTimer = setTimeout(() => {
          isReconnecting = false;
          if (eventSource) {
            eventSource.close();
            eventSource = null;
          }
          void connect();
        }, delay);
      } else {
        logger.error(
          "useMercureSubscription",
          `Failed to connect Mercure subscription after ${maxRetries} attempts`
        );
        connectionAttempts = Math.floor(maxRetries / 2);

        isReconnecting = true;
        reconnectTimer = setTimeout(() => {
          isReconnecting = false;
          if (eventSource) {
            eventSource.close();
            eventSource = null;
          }
          void connect();
        }, 60000); // Try again in 1 minute
      }
    };

    const handleVisibilityChange = () => {
      if (
        document.visibilityState === "visible" &&
        eventSource?.readyState !== EventSource.OPEN
      ) {
        logger.info(
          "useMercureSubscription",
          "App became visible, reconnecting Mercure subscription..."
        );
        connectionAttempts = 0;
        if (eventSource) {
          eventSource.close();
          eventSource = null;
        }
        void connect();
      }
    };

    const handleOnline = () => {
      if (eventSource?.readyState !== EventSource.OPEN) {
        logger.info(
          "useMercureSubscription",
          "Network restored, reconnecting Mercure subscription..."
        );
        connectionAttempts = 0;
        if (eventSource) {
          eventSource.close();
          eventSource = null;
        }
        void connect();
      }
    };

    // Initial connection
    void connect();

    // Add event listeners
    document.addEventListener("visibilitychange", handleVisibilityChange);
    window.addEventListener("online", handleOnline);

    return () => {
      isCancelled = true;
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      window.removeEventListener("online", handleOnline);

      if (reconnectTimer) {
        clearTimeout(reconnectTimer);
      }

      if (eventSource) {
        eventSource.close();
      }
    };
  }, [topic, eventsKey, restaurantId]);

  return event;
}

export function useRestaurantCurrency(): string {
  const restaurant = useAppSelector((state) => state.user.currentRestaurant);
  return restaurant?.currency?.iso3 ?? "XOF";
}
